import React from 'react';
import { AvKeyValue, QueryOrigin } from '@fabric-ui/components';
import { FormatterType, HeadCellType } from '@fabric-ui/components/Table';
import { Box, capitalize } from '@mui/material';
import { queryOptions } from '@tanstack/react-query';
import { format } from 'date-fns';
import z from 'zod/v4';
import { AvKeyValueWithTooltip } from '../../components/AvKeyValueWithTooltip';
import { IconVariant, ItemWithLogo } from '../../components/ItemWithLogo';
import { aql } from '../../utils/aql/aql.utils';
import { field, mergeAnd, SqlToFilterConverterColumnSchema } from '../../utils/filter';
import { SortDir, SortDirToSortType } from '../CustomDashboards/types';
import { queryObjectBuilder } from '../CustomDashboards/types/QueryObjectBuilder';
import { queryObjectRequestBuilder } from '../CustomDashboards/types/QueryObjectRequestBuilder';
import { ProjectionId } from '../Sources/types';
import type { State } from './Logs';
import { LOGS_CONST } from './Logs.const';
import { SourcesDistributionQuery } from './Logs.graphql';
import { parseLogsQuery, prepareDateFilter } from './Logs.utils.filter';
import { CreateQueryParams, isExternalProjectionTable, LogsQueryObjectResponse, responseSchema } from './Logs.utils.query';
import { LogsFacet, LogsTableRow } from './Logs.utils.sources';
import { flattenDocument } from './utils/flatten-document';

export const createSourcesDistributionQuery = ({
  api,
  date,
  projectionTable = LOGS_CONST.EXTERNAL_PROJECTION_TABLE,
}: Pick<CreateQueryParams, 'api'> & {
  date: Date[];
  projectionTable?: string;
}) => {
  const dimension = queryObjectBuilder.select.dim('source', 'source');
  const metrics = [
    queryObjectBuilder.select.metric(aql.count('*'), 'doc_count'),
    queryObjectBuilder.select.metric('MAX(time)', 'max_time'),
    queryObjectBuilder.select.metric('MIN(time)', 'min_time'),
  ];

  const dateFilter = field('time').date.between(date[0], date[1]);

  const builder = queryObjectBuilder
    .create({ builtIn: true, name: projectionTable })
    .setDimensions(dimension)
    .setMetrics(...metrics)
    .setGroupBy('source')
    .setFilter(dateFilter)
    .setSorting(queryObjectBuilder.sorting.desc('max_time'))
    .setLimit(100)
    .setGroupOthers(false);

  const queryObject = builder.build();
  const req = queryObjectRequestBuilder.create().setQuery(queryObject).setOrigin(QueryOrigin.INTERACTIVE_QUERY).build();

  return queryOptions({
    queryKey: ['SourcesDistributionQuery', queryObject] as const,
    queryFn: async () => {
      const res = await api(SourcesDistributionQuery, { options: { req } });
      if (res.errors) {
        throw res.errors;
      }
      return responseSchema
        .extend({
          rows: z
            .object({
              id: z.string(),
              data: z.object({
                source: z.string(),
                doc_count: z.number(),
                max_time: z.string(),
                min_time: z.string(),
              }),
            })
            .array(),
        })
        .parse(res.data?.queryObject)
        .rows.map(row => row.data.source);
    },
  });
};

export const avalorRowIdHeadCell: HeadCellType = {
  id: 'avalorRowId',
  label: 'ID',
  hidden: true,
  isKey: true,
  disableSortBy: true,
  disableFilter: true,
};

export const defaultExternalProjectionHeadCells: HeadCellType[] = [
  avalorRowIdHeadCell,
  {
    id: 'timestamp',
    type: FormatterType.date,
    label: 'Time',
    initWidth: 142,
    style: { '> div': { whiteSpace: 'nowrap' } },
    formatter: (_, { timestamp }: LogsTableRow) => (
      <Box display="flex" fontSize="13px" gap={8}>
        {formatLogTimestamp(timestamp)}
      </Box>
    ),
    disableFilter: true,
  },
  {
    id: 'source',
    label: 'Source',
    formatter: (_, row: LogsTableRow) => {
      const source = row.source ?? row.flatten.source ?? row.data.source;
      return source && typeof source === 'string' ? <ItemWithLogo variant={IconVariant.sourcesMapByName} type={source} /> : null;
    },
    disableFilter: true,
  },
  {
    id: 'document',
    label: 'Logs',
    sx: { width: '100%' },
    style: { '> div': { maxWidth: 'none' } },
    formatter: (_, { flatten }) => <AvKeyValue value={flatten} linesLimit={2} />,
    disableSortBy: true,
    disableFilter: true,
  },
];

export const defaultViewProjectionHeadCells = (): HeadCellType[] => [
  avalorRowIdHeadCell,
  {
    id: `timestamp`,
    type: FormatterType.date,
    label: 'Time',
    initWidth: 142,
    style: { '> div': { whiteSpace: 'nowrap' } },
    formatter: (_, { timestamp }: LogsTableRow) => (
      <Box display="flex" fontSize="13px" gap={8}>
        {formatLogTimestamp(timestamp)}
      </Box>
    ),
    disableFilter: true,
  },
  {
    id: 'source',
    label: 'Source',
    formatter: (_, row: LogsTableRow) => {
      const source = row.source ?? row.flatten.source ?? row.data.source;
      return source && typeof source === 'string' ? <ItemWithLogo variant={IconVariant.sourcesMapByName} type={source} /> : null;
    },
    disableFilter: true,
    disableSortBy: true,
  },
  {
    id: 'document',
    label: 'Logs',
    sx: { width: '100%' },
    style: { '> div': { maxWidth: 'none' } },
    formatter: (_, { flatten }) => <AvKeyValue value={flatten} linesLimit={2} />,
    disableSortBy: true,
    disableFilter: true,
  },
];

const externalProjectionFormatter = (row: LogsTableRow, columnId: string, facet?: LogsFacet) => {
  if (!facet) return String(row.flatten[columnId] ?? row.data[columnId] ?? '');
  if (row.data[facet.value] !== undefined) return String(row.data[facet.value]);
  if (row.flatten[facet.value] !== undefined) return String(row.flatten[facet.value]);
  return '';
};

const viewProjectionFormatter = (row: LogsTableRow, columnId: string) => {
  if (row.data[columnId] !== undefined) {
    return String(row.data[columnId]);
  }
  if (row.data[columnId.replace(`${row.source}.`, '')] !== undefined) {
    return String(row.data[columnId.replace(`${row.source}.`, '')]);
  }
  if (row.flatten[columnId] !== undefined) {
    return String(row.flatten[columnId]);
  }
  if (row.flatten[columnId.replace(`${row.source}.`, '')] !== undefined) {
    return String(row.flatten[columnId.replace(`${row.source}.`, '')]);
  }
  return '';
};

export const createHeadCells = (
  selectedIds: string[],
  facets: LogsFacet[],
  defaultHeadCells: HeadCellType[] = [],
  sort?: { id: string; dir: SortDir },
  isFirstParty = false,
  highlightTerms?: string[]
): HeadCellType[] =>
  selectedIds
    .map(id => {
      const defaultCell = defaultHeadCells.find(cell => cell.id === id);
      if (defaultCell) {
        if (id === 'document' && highlightTerms?.length) {
          return {
            ...defaultCell,
            formatter: (_, { flatten }: LogsTableRow) => (
              <AvKeyValueWithTooltip value={flatten} linesLimit={2} highlightText={highlightTerms} />
            ),
          };
        }
        return defaultCell;
      }
      const facet = facets.find(f => f.id === id);
      const columnId = facet?.id || id;
      const label = capitalize(facet?.displayName || id);
      const isHidden = !selectedIds.includes(id);
      return {
        id: columnId,
        label,
        hidden: isHidden,
        // TODO: Filter tables disabled until https://jira.corp.zscaler.com/browse/AVA-25066 is fixed
        disableFilter: true,
        ...(isFirstParty && !['time', 'timestamp', 'source'].includes(columnId) && { disableSortBy: true }),
        formatter: (_: unknown, row: LogsTableRow) =>
          isFirstParty ? viewProjectionFormatter(row, columnId) : externalProjectionFormatter(row, columnId, facet),
      };
    })
    .map(cell => (sort && cell.id === sort.id ? { ...cell, sort: SortDirToSortType[sort.dir] } : cell));

export const facetsToColumTypeMap = (facets: LogsFacet[]): SqlToFilterConverterColumnSchema =>
  facets.reduce<SqlToFilterConverterColumnSchema>((acc, curr) => ({ ...acc, [curr.id]: curr.columnMap }), {});

export const transformExternalProjection = (data: LogsQueryObjectResponse): LogsTableRow[] =>
  data.rows.map((row, i) => {
    const document = z.record(z.string(), z.json()).parse(row.data.document || row.data.DOCUMENT);
    return {
      avalorRowId: i,
      data: document,
      timestamp: z.string().parse(row.data.timestamp || row.data.time),
      source: z.string().parse(row.data.source),
      flatten: flattenDocument(document),
      hash: row.id,
    };
  });

export const transformViewProjection = (data: LogsQueryObjectResponse, table: string): LogsTableRow[] =>
  data.rows.map((row, i) => {
    const document = z.record(z.string(), z.json()).parse(row.data);

    return {
      avalorRowId: i,
      data: document,
      timestamp: z.string().parse(row.data.timestamp || row.data.event_timestamp),
      source: table,
      flatten: flattenDocument(document),
      hash: row.id,
    };
  });

const prepareFilters = (
  date: State['searchState']['date'],
  query: string,
  table: string,
  columnTypeMap: SqlToFilterConverterColumnSchema
) => {
  const dateFilter = prepareDateFilter(date, !isExternalProjectionTable(table), table);
  const queryFilter = parseLogsQuery(columnTypeMap, table, query);
  return queryFilter ? mergeAnd(dateFilter, queryFilter) : dateFilter;
};

export const createExternalProjectionQueryObject = (params: {
  columnTypeMap: SqlToFilterConverterColumnSchema;
  table: string;
  query: string;
  page: number;
  date: State['searchState']['date'];
  sort: State['tabs'][number]['sort'];
  projectionTable?: string;
}) => {
  const { sort, date, query, table, columnTypeMap, page, projectionTable = LOGS_CONST.EXTERNAL_PROJECTION_TABLE } = params;
  const { dir = SortDir.DESC, id: sortId = 'timestamp' } = sort || {};

  const dimensions = [
    queryObjectBuilder.select.dim('document', 'document'),
    queryObjectBuilder.select.dim('source', 'source'),
    queryObjectBuilder.select.dim('time', 'timestamp'),
  ];

  const getSortId = () => {
    if (dimensions.some(d => d.alias === sortId)) {
      return sortId;
    }
    const sortDimension = dimensions.find(d => d.name === sortId);
    if (sortDimension) {
      return sortDimension.alias || sortDimension.name;
    }

    dimensions.push(queryObjectBuilder.select.dim(sortId));
    return sortId;
  };

  const sortDir = SortDirToSortType[dir];
  const sorting = queryObjectBuilder.sorting[sortDir](getSortId());

  return queryObjectBuilder
    .create({ builtIn: true, name: projectionTable })
    .setDimensions(...dimensions)
    .setSorting(sorting)
    .setFilter(prepareFilters(date, query, table, columnTypeMap))
    .setLimit(LOGS_CONST.TABLE_PAGE_SIZE)
    .setOffset(page * LOGS_CONST.TABLE_PAGE_SIZE)
    .setGroupOthers(false)
    .build();
};

export const createViewProjectionQueryObject = (params: {
  columnTypeMap: SqlToFilterConverterColumnSchema;
  projection: ProjectionId;
  query: string;
  page: number;
  date: State['searchState']['date'];
  sort: State['tabs'][number]['sort'];
}) => {
  const { projection, query, date, page, columnTypeMap } = params;

  const ensureProjectionPrefix = (dimName: string) =>
    dimName.startsWith(`${projection.name}.`) ? dimName : `${projection.name}.${dimName}`;

  const createAlias = (dimName: string) => dimName.replace(`${projection.name}.`, '');
  const dimensions = Object.keys(columnTypeMap).map(dimName =>
    ensureProjectionPrefix(dimName) === ensureProjectionPrefix(LOGS_CONST.VIEW_PROJECTION_TIMESTAMP_NAME)
      ? queryObjectBuilder.select.dim(ensureProjectionPrefix(dimName), createAlias('timestamp'))
      : queryObjectBuilder.select.dim(ensureProjectionPrefix(dimName), createAlias(dimName))
  );

  const dir = SortDirToSortType[params.sort?.dir || SortDir.DESC];
  const sortId = params.sort?.id && params.sort.id !== 'timestamp' ? createAlias(ensureProjectionPrefix(params.sort.id)) : 'timestamp';
  const sorting = queryObjectBuilder.sorting[dir](sortId);

  return queryObjectBuilder
    .create({ builtIn: projection.builtIn, name: projection.name })
    .setDimensions(...dimensions)
    .setSorting(sorting)
    .setFilter(prepareFilters(date, query, projection.name, columnTypeMap))
    .setLimit(LOGS_CONST.TABLE_PAGE_SIZE)
    .setOffset(page * LOGS_CONST.TABLE_PAGE_SIZE)
    .setGroupOthers(false)
    .build();
};

export function formatLogTimestamp(timestamp: string): string {
  return format(new Date(timestamp), 'MMM dd kk:mm:ss');
}
